const POSITIVE = "positive safe integer";

export function assertPositive(value: unknown, label = POSITIVE): asserts value is number {
  if (!Number.isSafeInteger(value) || (value as number) < 1 || Object.is(value, -0)) {
    throw new TypeError(`${label} must be a positive safe integer`);
  }
}

export function assertNonNegative(value: unknown, label: string): asserts value is number {
  if (!Number.isSafeInteger(value) || (value as number) < 0 || Object.is(value, -0)) {
    throw new TypeError(`${label} must be a non-negative safe integer`);
  }
}

export function assertSafe(value: unknown, label: string): asserts value is number {
  if (!Number.isSafeInteger(value) || Object.is(value, -0)) {
    throw new TypeError(`${label} must be a safe integer`);
  }
}

export function assertRecord(value: unknown, keys: readonly string[], label: string): asserts value is Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new TypeError(`${label} must be a record`);
  }
  const prototype = Object.getPrototypeOf(value) as object | null;
  if (prototype !== Object.prototype && prototype !== null) {
    throw new TypeError(`${label} must be plain data`);
  }
  const actual = Reflect.ownKeys(value);
  if (actual.length !== keys.length || actual.some((key, index) => key !== keys[index])) {
    throw new TypeError(`${label} has a noncanonical field vector`);
  }
  for (const key of actual) {
    const descriptor = Object.getOwnPropertyDescriptor(value, key);
    if (typeof key !== "string" || descriptor === undefined || !descriptor.enumerable || !("value" in descriptor)) {
      throw new TypeError(`${label} must have enumerable data fields`);
    }
  }
}

export function assertArray(value: unknown, label: string): asserts value is readonly unknown[] {
  if (!Array.isArray(value)) throw new TypeError(`${label} must be an array`);
  for (let index = 0; index < value.length; index += 1) {
    if (!Object.hasOwn(value, index)) throw new TypeError(`${label} cannot contain holes`);
  }
}

export function assertBoolean(value: unknown, label: string): asserts value is boolean {
  if (typeof value !== "boolean") throw new TypeError(`${label} must be a boolean`);
}

export function assertEnum<const Value extends string>(
  value: unknown,
  values: readonly Value[],
  label: string,
): asserts value is Value {
  if (typeof value !== "string" || !values.includes(value as Value)) {
    throw new TypeError(`${label} is outside its closed enum`);
  }
}

export function isDeepFrozenPlain(value: unknown, ancestors = new Set<object>()): boolean {
  if (value === null || typeof value === "string" || typeof value === "boolean") return true;
  if (typeof value === "number") return Number.isFinite(value) && !Object.is(value, -0);
  if (typeof value !== "object" || !Object.isFrozen(value) || ancestors.has(value)) return false;
  const prototype = Object.getPrototypeOf(value) as object | null;
  if (!Array.isArray(value) && prototype !== Object.prototype && prototype !== null) return false;
  const next = new Set(ancestors).add(value);
  return Reflect.ownKeys(value).every((key) => {
    if (Array.isArray(value) && key === "length") return true;
    if (typeof key !== "string") return false;
    const descriptor = Object.getOwnPropertyDescriptor(value, key);
    return descriptor !== undefined && descriptor.enumerable && "value" in descriptor &&
      isDeepFrozenPlain(descriptor.value, next);
  });
}

export function assertDeepFrozenPlain(value: unknown, label: string): void {
  if (!isDeepFrozenPlain(value)) throw new TypeError(`${label} must be recursively frozen plain data`);
}

export function deepEqual(left: unknown, right: unknown): boolean {
  if (Object.is(left, right)) return true;
  if (typeof left !== "object" || left === null || typeof right !== "object" || right === null) return false;
  if (Array.isArray(left) !== Array.isArray(right)) return false;
  const leftKeys = Reflect.ownKeys(left);
  const rightKeys = Reflect.ownKeys(right);
  if (leftKeys.length !== rightKeys.length || leftKeys.some((key, index) => key !== rightKeys[index])) return false;
  return leftKeys.every((key) => deepEqual(Reflect.get(left, key), Reflect.get(right, key)));
}

export function cloneFreeze<Value>(value: Value, ancestors = new Set<object>()): Readonly<Value> {
  if (value === null || typeof value === "string" || typeof value === "boolean") return value as Readonly<Value>;
  if (typeof value === "number") {
    if (!Number.isFinite(value)) throw new TypeError("plain data number must be finite");
    return value as Readonly<Value>;
  }
  if (typeof value !== "object") throw new TypeError("authority accepts only plain data");
  if (ancestors.has(value)) throw new TypeError("plain data cannot contain cycles");
  const next = new Set(ancestors).add(value);
  if (Array.isArray(value)) {
    return Object.freeze(value.map((entry) => cloneFreeze(entry, next))) as unknown as Readonly<Value>;
  }
  const prototype = Object.getPrototypeOf(value) as object | null;
  if (prototype !== Object.prototype && prototype !== null) throw new TypeError("authority record must be plain data");
  const output: Record<string, unknown> = {};
  for (const key of Reflect.ownKeys(value)) {
    if (typeof key !== "string") throw new TypeError("symbol fields are forbidden");
    const descriptor = Object.getOwnPropertyDescriptor(value, key);
    if (descriptor === undefined || !descriptor.enumerable || !("value" in descriptor)) {
      throw new TypeError("authority fields must be enumerable data values");
    }
    Object.defineProperty(output, key, {
      value: cloneFreeze(descriptor.value, next),
      enumerable: true,
      configurable: false,
      writable: false,
    });
  }
  return Object.freeze(output) as Readonly<Value>;
}

export function checkedAdd(left: number, right: number, label: string): number {
  assertSafe(left, label);
  assertSafe(right, label);
  const result = left + right;
  assertSafe(result, label);
  return result;
}

export function compareDistance(left: string, right: string): number {
  if (!/^(?:0|[1-9][0-9]*)$/u.test(left) || !/^(?:0|[1-9][0-9]*)$/u.test(right)) {
    throw new TypeError("distance must be canonical decimal");
  }
  return left.length - right.length || (left < right ? -1 : left > right ? 1 : 0);
}
